/**
 * Lettrova API client — thin JWT-aware fetch wrapper.
 *
 * The token comes from `RumioSessionProvider` (the iframe session JWT). A
 * tiny module-level setter (`setTokenGetter`) lets the provider register a
 * lazy getter, so `api.get/post/put/del/upload` callers don't have to thread
 * the token through every layer.
 *
 * A 401 (expired JWT) gets a fresh token and retries the request once, so a
 * long-open tab keeps working without a reload. By default the fresh token is
 * requested from the parent frame; the session provider swaps in its own way
 * of refreshing for a standalone session (`setTokenRefresher`).
 */

import { requestSessionRefresh } from "./iframe-bridge";

let getToken: () => string | null = () => null;

export function setTokenGetter(fn: () => string | null) {
  getToken = fn;
}

export function getFreshToken(): string | null {
  return getToken();
}

type TokenRefresher = (previous: string | null) => Promise<string | null>;
let customRefresher: TokenRefresher | null = null;

/** Replace how a 401 is recovered from. Pass null to go back to asking the parent frame. */
export function setTokenRefresher(fn: TokenRefresher | null) {
  customRefresher = fn;
}

export class ApiError extends Error {
  status: number;
  /** Machine-readable code from the body's `error` field (e.g. "quota_exceeded"). */
  code: string | null;
  body: unknown;
  /** The request never reached the app, or reached it mid-deploy: the same request may well work a moment later. */
  retryable: boolean;
  constructor(status: number, message: string, body: unknown, code: string | null = null, retryable = false) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
    this.retryable = retryable;
  }
}

/** Human-readable text for an error; falls back to the given default. */
export function errorText(err: unknown, fallback = "Something went wrong."): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** True when trying the same thing again is a reasonable answer to this error. */
export function isRetryable(err: unknown): boolean {
  return err instanceof ApiError && err.retryable;
}

// The proxy answers these itself while the app is being swapped or is unreachable, so the
// body is the proxy's, not ours. Status 0 is a request that never got an answer at all.
const UNAVAILABLE = "Lettrova is briefly unavailable, most likely a deploy. Try again in a moment.";
const OFFLINE = "Lettrova can't be reached. Check your connection and try again.";

// The backend puts a stable code in `error` and the human sentence in
// `message`; show the sentence, keep the code for branching.
const FRIENDLY: Record<string, string> = {
  unauthorized: "Your session has expired. Reload the page to sign in again.",
  billing_not_configured: "Signup isn't open yet.",
  signin_not_configured: "Signing in isn't available yet.",
  rate_limited: "Too many attempts. Try again in a little while.",
  not_found: "That item no longer exists.",
  internal_error: "Something went wrong on our side. Try again in a moment.",
  payload_too_large: "That upload is too large.",
  validation_failed: "Some fields are invalid. Check the form and try again.",
  owner_only: "Only the account's owner can do that.",
  rumio_billed: "This account is billed through your site account, so plan and card changes are made there.",
};

/** "send_at" → "Send at"; "brand.reply_to" → "Reply to". */
function fieldLabel(field: string): string {
  const last = field.split(".").filter(Boolean).pop() ?? "";
  const words = last.replace(/_/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : "";
}

/** The first field the server refused, with its reason: "Send at: Invalid date". */
function validationText(obj: Record<string, unknown>): string | null {
  const errors = Array.isArray(obj.errors) ? (obj.errors as { field?: unknown; message?: unknown }[]) : [];
  const first = errors.find((e) => typeof e?.message === "string");
  if (!first) return null;
  const label = typeof first.field === "string" ? fieldLabel(first.field) : "";
  const more = errors.length > 1 ? ` (and ${errors.length - 1} more)` : "";
  return `${label ? `${label}: ` : ""}${first.message as string}${more}`;
}

async function parseError(resp: Response): Promise<ApiError> {
  const text = await resp.text();
  let parsed: unknown = text;
  try {
    parsed = JSON.parse(text);
  } catch {
    // text body
  }
  const obj = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  const code = typeof obj?.error === "string" ? obj.error : null;
  // The proxy's own answer (a gateway status with no code of ours, or any 5xx that isn't
  // JSON) or the app's "draining" / "db_unavailable": nothing is wrong with the request,
  // and the sentence should say so instead of quoting an HTTP code. A gateway status the
  // app sent itself carries its own sentence ("Your email service refused this: …"),
  // which is shown as it is.
  const gateway = resp.status === 502 || resp.status === 503 || resp.status === 504;
  const passing = code === "draining" || code === "db_unavailable";
  if (passing || (gateway && !code) || (!obj && resp.status >= 500)) {
    return new ApiError(resp.status, UNAVAILABLE, parsed, code, true);
  }
  const message =
    (typeof obj?.message === "string" && obj.message) ||
    (code === "validation_failed" && obj && validationText(obj)) ||
    (code && FRIENDLY[code]) ||
    code ||
    `Request failed (HTTP ${resp.status})`;
  // A 503 from the app is a service of ours that is down for now (the AI writer, the assistant).
  return new ApiError(resp.status, message, parsed, code, resp.status === 503);
}

/** Ask the parent for a new JWT and wait (briefly) for it to land. */
async function refreshedToken(previous: string | null, timeoutMs = 5000): Promise<string | null> {
  requestSessionRefresh();
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 100));
    const t = getToken();
    if (t && t !== previous) return t;
  }
  return null;
}

async function send(path: string, init: RequestInit, retried = false): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  let resp: Response;
  try {
    resp = await fetch(path, { ...init, headers, credentials: "include" });
  } catch {
    // The browser gave up before any answer: offline, DNS, or the connection dropped.
    throw new ApiError(0, OFFLINE, null, "network", true);
  }
  if (resp.status === 401 && !retried) {
    const fresh = await (customRefresher ? customRefresher(token) : refreshedToken(token));
    if (fresh) return send(path, init, true);
  }
  return resp;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const resp = await send(path, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) throw await parseError(resp);
  if (resp.status === 204) return undefined as unknown as T;
  return (await resp.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  delete: <T>(path: string) => request<T>("DELETE", path),

  /**
   * Multipart upload (campaign attachments). Auth header is added but
   * Content-Type is left to the browser so the FormData boundary is set.
   */
  upload: async <T>(path: string, form: FormData): Promise<T> => {
    const resp = await send(path, { method: "POST", body: form });
    if (!resp.ok) throw await parseError(resp);
    if (resp.status === 204) return undefined as unknown as T;
    return (await resp.json()) as T;
  },

  /** Authenticated binary download (e.g. an attachment's bytes). */
  blob: async (path: string): Promise<Blob> => {
    const resp = await send(path, { method: "GET" });
    if (!resp.ok) throw await parseError(resp);
    return resp.blob();
  },
};
